import { useEffect, useRef, useState } from 'react';
import { skipToken, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  can,
  formatQty,
  type SuggestionRow,
  type SuggestionScope,
  type SuggestionPreviewResult,
  type SuggestionApplyInput,
  type SuggestionApplyResult,
  type ReorderWindowSetInput,
} from '@servwell/shared';
import { DataTable, type DataTableColumn } from '@/components/ui/data-table';
import { useTRPC, useTRPCClient } from '../../lib/trpc';
import { createIntentKey } from '../../lib/idempotency';
import { isStockBusy, refusalStatus } from '../../lib/refusal';
import { useSession } from '../session';
import { Sheet } from '../ui/Sheet';
import { Button } from '../ui/Button';
import { Notice } from '../ui/Badge';
import { Field, field } from '../create/Field';
import { ErrorState, shownState, StaleNotice, refreshFailed } from '../ui/QueryStates';
import { IconInventory } from '../icons';
import { useOnline } from '../ui/useConnection';
import { sourceLabel } from './VanTemplatePreview';

type Props = {
  initialScope?: SuggestionScope;
  onClose: () => void;
  onSetup?: () => void;
  onBusyChange?: (busy: boolean) => void;
};

export function ReorderSuggestionsSheet(props: Props) {
  const session = useSession();
  const owner = `${session.orgId}:${session.userId}:${session.role}`;
  const currentOwner = useRef(owner);
  currentOwner.current = owner;
  return (
    <SuggestionsOffice
      key={owner}
      {...props}
      stillOwner={() => currentOwner.current === owner}
    />
  );
}

function SuggestionsOffice({
  initialScope,
  onClose,
  onSetup,
  onBusyChange,
  stillOwner,
}: Props & { stillOwner: () => boolean }) {
  const trpc = useTRPC(),
    client = useTRPCClient(),
    qc = useQueryClient(),
    session = useSession(),
    online = useOnline();
  const [scope, setScope] = useState(initialScope),
    [offset, setOffset] = useState(0),
    [listRevision, setListRevision] = useState<string | undefined>(),
    [scopeOffset, setScopeOffset] = useState(0),
    [scopeRevision, setScopeRevision] = useState<string | undefined>();
  const allowed = can(session.role, 'inventory', 'manage');
  const windowQuery = useQuery(
    trpc.restocks.windowGet.queryOptions(undefined, {
      enabled: allowed,
      staleTime: 0,
      refetchOnMount: 'always',
    }),
  );
  const catalogue = useQuery(
    trpc.restocks.suggestionScopes.queryOptions(
      {
        offset: scopeOffset,
        ...(scopeRevision ? { expectedRevision: scopeRevision } : {}),
      },
      { enabled: allowed, staleTime: 0, refetchOnMount: 'always' },
    ),
  );
  const query = useQuery(
    trpc.restocks.suggestions.queryOptions(
      allowed && scope
        ? { scope, offset, ...(listRevision ? { expectedRevision: listRevision } : {}) }
        : skipToken,
      { staleTime: 0, refetchOnMount: 'always' },
    ),
  );
  const data = query.data,
    state = shownState(query, query.data);
  const [review, setReview] = useState<SuggestionPreviewResult | null>(null),
    [reviewFresh, setReviewFresh] = useState(false),
    [loadingReview, setLoadingReview] = useState(false),
    [refreshing, setRefreshing] = useState(false),
    [writing, setWriting] = useState(false);
  const [reviewPage, setReviewPage] = useState({ offset: 0, vanOffset: 0 });
  const [error, setError] = useState<Error | null>(null),
    [accepted, setAccepted] = useState<SuggestionApplyResult | null>(null);
  const keys = useRef(createIntentKey()),
    attempt = useRef<SuggestionApplyInput | null>(null),
    busy = useRef(false),
    sequence = useRef(0),
    alive = useRef(true);
  const save = useMutation(
    trpc.restocks.suggestionApply.mutationOptions({
      networkMode: 'always',
      retry: false,
    }),
  );
  const windowSave = useMutation(
    trpc.restocks.windowSet.mutationOptions({ networkMode: 'always', retry: false }),
  );
  const [windowChoice, setWindowChoice] = useState<60 | 90 | 180 | null>(null),
    [windowError, setWindowError] = useState<Error | null>(null);
  const windowKeys = useRef(createIntentKey()),
    windowAttempt = useRef<ReorderWindowSetInput | null>(null);
  const selectedWindow = windowChoice ?? windowQuery.data?.windowDays;
  const unsavedWindow = selectedWindow !== windowQuery.data?.windowDays;
  const freshWindow =
    !!windowQuery.data &&
    !windowQuery.isError &&
    windowQuery.isFetchedAfterMount &&
    !windowQuery.isFetching &&
    query.isFetchedAfterMount &&
    !query.isFetching &&
    !query.isError &&
    !unsavedWindow &&
    data?.window.revision === windowQuery.data.revision;
  const valid = (n = sequence.current) =>
    alive.current && stillOwner() && sequence.current === n;
  useEffect(() => {
    alive.current = true;
    return () => {
      alive.current = false;
      sequence.current++;
    };
  }, []);
  useEffect(() => {
    onBusyChange?.(
      save.isPending || windowSave.isPending || loadingReview || refreshing || writing,
    );
    return () => onBusyChange?.(false);
  }, [
    save.isPending,
    windowSave.isPending,
    loadingReview,
    refreshing,
    writing,
    onBusyChange,
  ]);
  const loadReview = async (
    selection: { kind: 'all' } | { kind: 'items'; itemIds: string[] },
    offset = 0,
    vanOffset = 0,
    expectedRevision = data?.revision,
  ) => {
    if (
      !scope ||
      !data ||
      !online ||
      !allowed ||
      busy.current ||
      attempt.current ||
      accepted ||
      !freshWindow
    )
      return;
    const n = ++sequence.current;
    setLoadingReview(true);
    setReviewFresh(false);
    setError(null);
    try {
      const next = await client.restocks.suggestionPreview.query({
        scope,
        selection,
        expectedRevision: expectedRevision!,
        offset,
        vanOffset,
      });
      if (valid(n)) {
        setReview(next);
        setReviewFresh(true);
        setReviewPage({ offset, vanOffset });
      }
    } catch (e) {
      if (valid(n))
        setError(e instanceof Error ? e : new Error('Could not review usage.'));
    } finally {
      if (valid(n)) setLoadingReview(false);
    }
  };
  const apply = async () => {
    if (!online || !allowed || busy.current || accepted || !valid()) return;
    let request: SuggestionApplyInput | null;
    try {
      request =
        attempt.current ??
        (review && reviewFresh && freshWindow
          ? {
              scope: review.scope,
              itemIds: review.itemIds,
              expectedRevision: review.revision,
              ...keys.current.forIntent([
                session.orgId,
                session.userId,
                'restocks.suggestionApply',
                {
                  scope: review.scope,
                  itemIds: review.itemIds,
                  expectedRevision: review.revision,
                },
              ]),
            }
          : null);
    } catch {
      setError(
        new Error(
          'The browser could not create a request key. No request was sent. Restore browser storage and try again.',
        ),
      );
      return;
    }
    if (!request) return;
    busy.current = true;
    setWriting(true);
    attempt.current = request;
    setError(null);
    const n = sequence.current;
    try {
      const receipt = await save.mutateAsync(request);
      if (!valid(n)) return;
      setAccepted(receipt);
      setReviewFresh(false);
      attempt.current = null;
      keys.current.clear();
      try {
        await Promise.all(
          [
            trpc.restocks.pathKey(),
            trpc.inventory.reorderPoints.queryKey(),
            trpc.inventory.lowStock.queryKey(),
            trpc.inventory.onHand.queryKey(),
          ].map((queryKey) => qc.invalidateQueries({ queryKey })),
        );
      } catch {
        if (valid(n))
          setError(
            new Error(
              'Applied successfully. Saved values could not refresh; retry the refresh before reviewing more.',
            ),
          );
      }
    } catch (e) {
      if (valid(n)) {
        setError(e instanceof Error ? e : new Error('Could not apply suggestions.'));
        setReviewFresh(false);
      }
    } finally {
      busy.current = false;
      if (valid(n)) setWriting(false);
    }
  };
  const refresh = async () => {
    if (busy.current || !online || !valid() || !scope) return;
    busy.current = true;
    setRefreshing(true);
    const n = ++sequence.current;
    setReviewFresh(false);
    setError(null);
    try {
      const fresh = await client.restocks.suggestions.query({ scope, offset: 0 });
      if (!valid(n)) return;
      qc.setQueryData(trpc.restocks.suggestions.queryKey({ scope, offset: 0 }), fresh);
      setOffset(0);
      setListRevision(undefined);
      attempt.current = null;
      keys.current.clear();
      setAccepted(null);
      setReview(null);
    } catch (e) {
      if (valid(n))
        setError(
          e instanceof Error
            ? e
            : new Error('Saved values did not load. The original request is retained.'),
        );
    } finally {
      busy.current = false;
      if (valid(n)) setRefreshing(false);
    }
  };
  const reloadOwners = async () => {
    if (busy.current || !online || !valid()) return;
    const n = ++sequence.current;
    try {
      const next = await client.restocks.suggestionScopes.query({ offset: 0 });
      if (valid(n)) {
        qc.setQueryData(trpc.restocks.suggestionScopes.queryKey({ offset: 0 }), next);
        setScopeOffset(0);
        setScopeRevision(undefined);
      }
    } catch (e) {
      if (valid(n))
        setError(e instanceof Error ? e : new Error('Value owners did not load.'));
    }
  };
  const locked =
    save.isPending || windowSave.isPending || loadingReview || refreshing || writing;
  const pageReview = (next: { offset: number; vanOffset: number }) => {
    if (review)
      void loadReview(
        { kind: 'items', itemIds: review.itemIds },
        next.offset,
        next.vanOffset,
        review.scopeRevision,
      );
  };
  const saveWindow = async () => {
    if (
      !online ||
      !allowed ||
      busy.current ||
      loadingReview ||
      !windowQuery.data ||
      !selectedWindow ||
      !valid()
    )
      return;
    let request: ReorderWindowSetInput;
    try {
      request = windowAttempt.current ?? {
        windowDays: selectedWindow,
        expectedRevision: windowQuery.data.revision,
        ...windowKeys.current.forIntent([
          session.orgId,
          session.userId,
          'restocks.windowSet',
          { windowDays: selectedWindow, expectedRevision: windowQuery.data.revision },
        ]),
      };
    } catch {
      setWindowError(
        new Error(
          'The browser could not create a window request key. No request was sent.',
        ),
      );
      return;
    }
    const n = ++sequence.current;
    busy.current = true;
    setWriting(true);
    windowAttempt.current = request;
    setWindowError(null);
    setReviewFresh(false);
    try {
      const receipt = await windowSave.mutateAsync(request);
      if (!valid(n)) return;
      qc.setQueryData(trpc.restocks.windowGet.queryKey(), receipt);
      windowAttempt.current = null;
      windowKeys.current.clear();
      setWindowChoice(null);
      setReview(null);
      try {
        await Promise.all([
          qc.invalidateQueries({ queryKey: trpc.restocks.suggestions.pathKey() }),
          qc.invalidateQueries({ queryKey: trpc.restocks.suggestionHints.pathKey() }),
        ]);
      } catch {
        if (valid(n))
          setWindowError(
            new Error(
              'Window saved. Usage could not refresh; reload before applying suggestions.',
            ),
          );
      }
    } catch (e) {
      if (valid(n))
        setWindowError(
          e instanceof Error ? e : new Error('Could not save the usage window.'),
        );
    } finally {
      busy.current = false;
      if (valid(n)) setWriting(false);
    }
  };
  const columns: DataTableColumn<SuggestionRow>[] = [
    {
      key: 'part',
      label: 'Part',
      value: (r) => r.itemName,
      cell: (r) => (
        <div className="min-w-40 max-w-52 whitespace-normal break-words">
          <p>{r.itemName}</p>
          <p className="text-xs text-muted-foreground">{sourceLabel(r.source)}</p>
        </div>
      ),
    },
    {
      key: 'current',
      label: 'Current Keep / Fill',
      cell: (r) =>
        `${formatQty(r.currentKeepHundredths)} / ${formatQty(r.currentFillHundredths)}`,
    },
    {
      key: 'suggested',
      label: 'Suggested Keep / Fill',
      cell: (r) =>
        r.suggestedKeepHundredths === null || r.suggestedFillHundredths === null
          ? '—'
          : `Suggest ${formatQty(r.suggestedKeepHundredths)} / ${formatQty(r.suggestedFillHundredths)}`,
    },
    {
      key: 'evidence',
      label: 'Recorded usage',
      cell: (r) => (
        <div className="min-w-64 max-w-80 whitespace-normal break-words">
          <p>
            {formatQty(r.evidence.netHundredths)} {r.unitLabel} net ·{' '}
            {r.evidence.positiveObservations} positive uses
          </p>
          <p>
            {r.evidence.historyDays === null
              ? 'History not recorded'
              : `${r.evidence.historyDays} recorded days`}{' '}
            · {r.evidence.sourceLabel}
          </p>
          <p className="font-medium">
            {
              {
                actionable: 'Suggestion ready',
                matches_use: 'Matches use',
                not_enough_usage: 'Not enough usage',
                blocked: 'Usage unavailable',
              }[r.status]
            }
          </p>
          <p>{r.explanation}</p>
          {r.followers.length ? (
            <details>
              <summary className="min-h-11 cursor-pointer py-3">
                Follower evidence ({r.followers.length})
              </summary>
              <ul className="space-y-2">
                {r.followers.map((f) => (
                  <li key={f.vanId}>
                    <p className="font-medium">{f.vanName}</p>
                    <p>{f.excludedReason ?? f.evidence.explanation}</p>
                    <p>
                      {formatQty(f.evidence.netHundredths)} net ·{' '}
                      {f.evidence.positiveObservations} positive uses ·{' '}
                      {f.evidence.historyDays === null
                        ? 'History not recorded'
                        : `${f.evidence.historyDays} recorded days`}
                    </p>
                  </li>
                ))}
              </ul>
            </details>
          ) : null}
        </div>
      ),
    },
    {
      key: 'review',
      label: 'Review',
      cell: (r) =>
        r.status === 'actionable' && !review ? (
          <Button
            variant="ghost"
            aria-label={`Review ${r.itemName}`}
            disabled={
              !freshWindow ||
              !online ||
              locked ||
              !!attempt.current ||
              !!accepted ||
              refreshFailed(query, data)
            }
            onClick={() => void loadReview({ kind: 'items', itemIds: [r.itemId] })}
          >
            Review part
          </Button>
        ) : null,
    },
  ];
  return (
    <Sheet
      open
      wide
      title="Usage suggestions"
      onClose={() => {
        if (!busy.current && !locked) onClose();
      }}
      className="[&_button]:min-h-11"
      footer={
        <div className="space-y-2">
          {!online ? (
            <Notice tone="warn">
              Offline. Reconnect to review or apply; no change is queued.
            </Notice>
          ) : null}
          {refreshing ? <p role="status">Reloading saved usage…</p> : null}
          {query.isError && !error ? (
            <Notice tone="danger" alert>
              {query.error.message}
            </Notice>
          ) : null}
          {error ? (
            <Notice tone="danger" alert>
              {error.message}
              {attempt.current ? (
                <p>
                  {isStockBusy(error)
                    ? 'Retry the original request when stock is free.'
                    : 'The original request is retained. Verify saved values and audit before starting a different review.'}
                </p>
              ) : null}
            </Notice>
          ) : null}
          {windowError ? (
            <Notice tone="danger" alert>
              {windowError.message} The window request is kept separately from any Apply
              request.
            </Notice>
          ) : null}
          {windowAttempt.current ? (
            <Button
              disabled={!online || locked || !allowed}
              onClick={() => void saveWindow()}
            >
              Retry original window save
            </Button>
          ) : null}
          {accepted ? (
            <Notice tone="success">
              Applied {accepted.changedItemIds.length} suggestion
              {accepted.changedItemIds.length === 1 ? '' : 's'}. No stock moved.
            </Notice>
          ) : null}
          {attempt.current ? (
            <Button disabled={!online || locked || !allowed} onClick={() => void apply()}>
              Retry original Apply
            </Button>
          ) : review ? (
            <Button
              disabled={
                !reviewFresh ||
                !freshWindow ||
                !online ||
                save.isPending ||
                windowSave.isPending ||
                loadingReview ||
                !allowed ||
                !!accepted
              }
              onClick={() => void apply()}
            >
              {save.isPending ? 'Applying…' : 'Apply reviewed changes'}
            </Button>
          ) : null}
          {error || accepted ? (
            <Button
              variant="ghost"
              disabled={!online || locked}
              onClick={() => void refresh()}
            >
              {attempt.current && refusalStatus(error) !== 409
                ? 'I checked saved values; review again'
                : 'Reload usage and review again'}
            </Button>
          ) : null}
          <Button variant="ghost" disabled={locked} onClick={onClose}>
            Close
          </Button>
        </div>
      }
    >
      {!can(session.role, 'inventory', 'manage') ? (
        <Notice tone="danger" alert>
          Your role no longer permits inventory changes.
        </Notice>
      ) : (
        <div className="space-y-4">
          <p className="text-sm">
            Review recorded usage before changing Keep and Fill. Applying suggestions
            moves no stock.
          </p>
          <Field label="Value owner">
            <select
              className={`${field} min-h-11`}
              disabled={locked || !!attempt.current}
              aria-label="Value owner"
              value={scope ? `${scope.kind}:${scope.id}` : ''}
              onChange={(e) => {
                const [kind, id] = e.target.value.split(':');
                sequence.current++;
                setReview(null);
                setReviewFresh(false);
                setError(null);
                setAccepted(null);
                setScope(id ? { kind: kind as SuggestionScope['kind'], id } : undefined);
                setOffset(0);
                setListRevision(undefined);
              }}
            >
              <option value="">Choose a template or location</option>
              {scope &&
              !catalogue.data?.rows.some(
                (r) => r.scope.kind === scope.kind && r.scope.id === scope.id,
              ) ? (
                <option value={`${scope.kind}:${scope.id}`}>
                  {data?.scopeName ?? 'Selected value owner'}
                </option>
              ) : null}
              {catalogue.data?.rows.map((r) => (
                <option
                  key={`${r.scope.kind}:${r.scope.id}`}
                  value={`${r.scope.kind}:${r.scope.id}`}
                >
                  {r.name} · {r.scope.kind}
                  {!r.available && r.explanation ? ` · ${r.explanation}` : ''}
                </option>
              ))}
            </select>
          </Field>
          {catalogue.isError ? (
            <Notice tone="danger" alert>
              Value owners could not load.{' '}
              <Button
                variant="ghost"
                disabled={locked}
                onClick={() => void reloadOwners()}
              >
                Reload value owners
              </Button>
            </Notice>
          ) : null}
          {catalogue.data && (scopeOffset > 0 || catalogue.data.nextOffset !== null) ? (
            <div className="flex flex-wrap gap-2">
              <Button
                variant="ghost"
                disabled={scopeOffset === 0 || locked}
                onClick={() => {
                  setScopeRevision(catalogue.data!.revision);
                  setScopeOffset(Math.max(0, scopeOffset - 25));
                }}
              >
                Previous owners
              </Button>
              <Button
                variant="ghost"
                disabled={catalogue.data.nextOffset === null || locked}
                onClick={() => {
                  setScopeRevision(catalogue.data!.revision);
                  setScopeOffset(catalogue.data!.nextOffset!);
                }}
              >
                Next owners
              </Button>
            </div>
          ) : null}
          {windowQuery.isError ? (
            <Notice tone="danger" alert>
              Usage window could not load.{' '}
              <Button
                variant="ghost"
                disabled={locked || !online}
                onClick={() => void windowQuery.refetch()}
              >
                Reload usage window
              </Button>
            </Notice>
          ) : null}
          {windowQuery.data ? (
            <fieldset className="space-y-2" disabled={locked || !!windowAttempt.current}>
              <legend className="text-sm font-medium">Usage window</legend>
              <div className="flex flex-wrap gap-2">
                {([60, 90, 180] as const).map((days) => (
                  <Button
                    variant="ghost"
                    key={days}
                    aria-pressed={selectedWindow === days}
                    onClick={() => {
                      setWindowChoice(days);
                      setReviewFresh(false);
                    }}
                  >
                    {days + ' days'}
                  </Button>
                ))}
              </div>
              <p className="text-xs">
                {windowQuery.data.timezone} · current saved window{' '}
                {windowQuery.data.windowDays} days
              </p>
              {unsavedWindow ? (
                <p role="status">
                  Unsaved window. Save it, then review fresh usage before applying.
                </p>
              ) : null}
              <Button
                disabled={!unsavedWindow || !online || locked || !!windowAttempt.current}
                onClick={() => void saveWindow()}
              >
                Save window
              </Button>
            </fieldset>
          ) : null}
          {!scope ? (
            <p>Choose where the Keep and Fill values are owned.</p>
          ) : state === 'loading' ? (
            <p>Loading recorded usage…</p>
          ) : state === 'error' || state === 'offline' ? (
            <ErrorState
              icon={IconInventory}
              what="usage suggestions"
              onRetry={() => void refresh()}
            />
          ) : data ? (
            <>
              <h2 className="font-heading text-lg font-semibold">{data.scopeName}</h2>
              <p className="text-sm">
                {data.window.startDate} – {data.window.endDate} · {data.window.timezone}
              </p>
              <p className="text-xs text-muted-foreground">
                Recorded as of {data.window.asOf}
              </p>
              <StaleNotice
                show={refreshFailed(query, data)}
                onRetry={() => void refresh()}
              />
              {data.inheritedScopes.map((owner) => (
                <div
                  key={owner.scope.id}
                  className="space-y-1 rounded border border-border p-3"
                >
                  <p>
                    {owner.pairCount} inherited pairs are owned by {owner.name}.
                  </p>
                  <Button
                    variant="ghost"
                    disabled={locked || !!attempt.current || !!windowAttempt.current}
                    onClick={() => {
                      sequence.current++;
                      setScope(owner.scope);
                      setOffset(0);
                      setListRevision(undefined);
                      setReview(null);
                      setReviewFresh(false);
                      setAccepted(null);
                      setError(null);
                    }}
                  >
                    Review template {owner.name}
                  </Button>
                </div>
              ))}
              <DataTable
                columns={columns}
                rows={data.rows}
                rowKey={(r) => r.itemId}
                empty={
                  <div className="space-y-2">
                    <p>
                      Nothing watched here. Set up Keep and Fill in Stock or Van
                      templates.
                    </p>
                    <Button
                      variant="ghost"
                      disabled={locked}
                      onClick={onSetup ?? onClose}
                    >
                      Set up watched parts
                    </Button>
                  </div>
                }
              />
              <div className="flex flex-wrap gap-2">
                <Button
                  variant="ghost"
                  disabled={offset === 0 || locked}
                  onClick={() => {
                    setListRevision(data.revision);
                    setOffset(Math.max(0, offset - 25));
                  }}
                >
                  Previous parts
                </Button>
                <Button
                  variant="ghost"
                  disabled={data.nextOffset === null || locked}
                  onClick={() => {
                    setListRevision(data.revision);
                    setOffset(data.nextOffset!);
                  }}
                >
                  Next parts
                </Button>
              </div>
              <Button
                disabled={
                  !data.counts.actionable ||
                  !freshWindow ||
                  !online ||
                  locked ||
                  !!attempt.current ||
                  !!accepted ||
                  refreshFailed(query, data)
                }
                onClick={() => void loadReview({ kind: 'all' })}
              >
                Review all
              </Button>
              {review ? (
                <section className="space-y-3 border-t border-border pt-3">
                  <h3 className="font-semibold">Review changes to {review.scopeName}</h3>
                  <p>
                    {review.itemIds.length} selected parts · {review.affectedVanCount}{' '}
                    affected vans · {review.localExclusionCount} Local exceptions kept
                  </p>
                  <ul>
                    {review.affectedVans.map((v) => (
                      <li key={v.vanId}>{v.vanName}</li>
                    ))}
                  </ul>
                  <div className="flex flex-wrap gap-2">
                    <Button
                      variant="ghost"
                      disabled={!reviewPage.vanOffset || locked || !!attempt.current}
                      onClick={() =>
                        pageReview({
                          ...reviewPage,
                          vanOffset: Math.max(0, reviewPage.vanOffset - 25),
                        })
                      }
                    >
                      Previous affected vans
                    </Button>
                    <Button
                      variant="ghost"
                      disabled={
                        review.nextVanOffset === null || locked || !!attempt.current
                      }
                      onClick={() =>
                        pageReview({ ...reviewPage, vanOffset: review.nextVanOffset! })
                      }
                    >
                      Next affected vans
                    </Button>
                  </div>
                  <DataTable
                    columns={columns}
                    rows={review.rows}
                    rowKey={(r) => r.itemId}
                    empty={<p>No changes selected.</p>}
                  />
                  <div className="flex flex-wrap gap-2">
                    <Button
                      variant="ghost"
                      disabled={!reviewPage.offset || locked || !!attempt.current}
                      onClick={() =>
                        pageReview({
                          ...reviewPage,
                          offset: Math.max(0, reviewPage.offset - 25),
                        })
                      }
                    >
                      Previous reviewed parts
                    </Button>
                    <Button
                      variant="ghost"
                      disabled={review.nextOffset === null || locked || !!attempt.current}
                      onClick={() =>
                        pageReview({ ...reviewPage, offset: review.nextOffset! })
                      }
                    >
                      Next reviewed parts
                    </Button>
                  </div>
                </section>
              ) : null}
            </>
          ) : null}
        </div>
      )}
    </Sheet>
  );
}
